import React, { useState } from "react";
import { Spinner } from "../../../atoms/Spinner";
import { DataTable } from "../../../atoms/DataTable";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { EmptyState } from "../../../atoms/EmptyState";
import MessageContentDrawer from "./MessageContentDrawer";
import {
  useTwilioMessages,
  useTwilioMessageDetail,
} from "../../../../hooks/providers";
import { describeTwilioError } from "../../../../utils/twilioErrorCodes";
import Pagination from "../../../public/Pagination";

const PAGE_SIZE = 20;

const statusTone = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "delivered" || s === "sent" || s === "received") return "success";
  if (s === "queued" || s === "sending" || s === "accepted") return "warning";
  if (s === "undelivered" || s === "failed") return "failure";
  return "gray";
};

const TwilioMessagesPanel: React.FC<{ customer: string; entity: string }> = ({
  customer,
  entity,
}) => {
  // Twilio paginates by page token, not offset; keep the visited tokens so Prev can go back.
  const [tokenStack, setTokenStack] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [selectedSid, setSelectedSid] = useState<string | null>(null);

  const pageToken = tokenStack[tokenStack.length - 1];
  const params: Record<string, any> = { limit: PAGE_SIZE };
  if (pageToken) params.page_token = pageToken;

  const { data, isLoading } = useTwilioMessages(customer, entity, params);
  const detail = useTwilioMessageDetail(
    customer,
    entity,
    selectedSid,
    !!selectedSid
  );

  const messages: any[] = data?.messages ?? [];
  const nextToken: string | undefined = data?.next_page_token || undefined;
  const hasMore: boolean = Boolean(data?.has_more && nextToken);
  const canGoBack = tokenStack.length > 1;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Carrier delivery log from Twilio (WhatsApp and SMS), {PAGE_SIZE} per
        page. This is the delivery outcome per message, distinct from the KMM
        queue (internal state before handoff). Click a row to see its content.
      </p>

      {isLoading ? (
        <Spinner size="sm" />
      ) : messages.length === 0 ? (
        <EmptyState title="No message" bordered />
      ) : (
        <>
          <DataTable>
            <DataTable.Head>
              <DataTable.HeadCell>Date</DataTable.HeadCell>
              <DataTable.HeadCell>Channel</DataTable.HeadCell>
              <DataTable.HeadCell>To</DataTable.HeadCell>
              <DataTable.HeadCell>Status</DataTable.HeadCell>
              <DataTable.HeadCell>Error</DataTable.HeadCell>
              <DataTable.HeadCell numeric>Price</DataTable.HeadCell>
            </DataTable.Head>
            <DataTable.Body>
              {messages.map((m) => (
                <DataTable.Row
                  key={m.sid}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedSid(m.sid)}
                >
                  <DataTable.Cell className="text-gray-600">
                    {m.date_sent ? new Date(m.date_sent).toLocaleString() : "—"}
                  </DataTable.Cell>
                  <DataTable.Cell>{m.channel}</DataTable.Cell>
                  <DataTable.Cell>{m.to}</DataTable.Cell>
                  <DataTable.Cell>
                    <StatusBadge color={statusTone(m.status)} size="xs">
                      {m.status}
                    </StatusBadge>
                  </DataTable.Cell>
                  <DataTable.Cell className="text-xs text-red-600">
                    {m.error_code ? (
                      <span
                        className="cursor-help underline decoration-dotted"
                        title={describeTwilioError(
                          m.error_code,
                          m.error_message
                        )}
                      >
                        {m.error_code}
                      </span>
                    ) : (
                      ""
                    )}
                  </DataTable.Cell>
                  <DataTable.Cell numeric className="text-gray-600">
                    {m.price ? `${m.price} ${m.price_unit || ""}` : "—"}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable>

          <div className="mt-2 flex items-center justify-end">
            <Pagination
              mode="cursor"
              canGoBack={canGoBack}
              hasMore={hasMore}
              onPrevious={() =>
                setTokenStack((stack) =>
                  stack.length > 1 ? stack.slice(0, -1) : stack
                )
              }
              onNext={() => setTokenStack((stack) => [...stack, nextToken])}
              label={`Page ${tokenStack.length}`}
            />
          </div>
        </>
      )}

      <MessageContentDrawer
        open={!!selectedSid}
        onClose={() => setSelectedSid(null)}
        title="Message content"
        isLoading={detail.isLoading}
        data={detail.data}
      />
    </div>
  );
};

export default TwilioMessagesPanel;
